import React, { useMemo } from 'react';
import { DetectedObject } from '../lib/detectApi';

export interface ObjectSelectorProps {
  imageUrl: string;
  objects: DetectedObject[] | null; // null = not yet loaded
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  loading: boolean;
  error: string;
  onRefresh: () => void;
  maxSelect?: number;
}

/** Only the N largest objects (by box area) get a dot on the image. */
const MAX_DOTS = 5;

interface Dot {
  obj: DetectedObject;
  x: number; // percent of image width
  y: number; // percent of image height
  labelBelow: boolean;
}

function boxArea(o: DetectedObject): number {
  return (o.box.xmax - o.box.xmin) * (o.box.ymax - o.box.ymin);
}

/** Top-N objects by area, dot at each box center, nudged apart so dots never overlap. */
export function computeDots(objects: DetectedObject[], maxDots = MAX_DOTS): Dot[] {
  const dots: Dot[] = [...objects]
    .sort((a, b) => boxArea(b) - boxArea(a))
    .slice(0, maxDots)
    .map(o => ({
      obj: o,
      x: (o.box.xmin + o.box.xmax) / 20, // 0–1000 box → percent, center
      y: (o.box.ymin + o.box.ymax) / 20,
      labelBelow: false,
    }));

  // ponytail: naive O(n²) pairwise repulsion, n ≤ 5 — replace with a real
  // layout solver only if dot count ever grows.
  const MIN_DIST = 8; // percent of image
  for (let pass = 0; pass < 10; pass++) {
    let moved = false;
    for (let i = 0; i < dots.length; i++) {
      for (let j = i + 1; j < dots.length; j++) {
        const dx = dots[j].x - dots[i].x;
        const dy = dots[j].y - dots[i].y;
        const dist = Math.hypot(dx, dy);
        if (dist >= MIN_DIST) continue;
        const push = (MIN_DIST - dist) / 2 + 0.1;
        const ux = dist > 0 ? dx / dist : 1;
        const uy = dist > 0 ? dy / dist : 0;
        dots[i].x -= ux * push; dots[i].y -= uy * push;
        dots[j].x += ux * push; dots[j].y += uy * push;
        moved = true;
      }
    }
    if (!moved) break;
  }

  for (const d of dots) {
    d.x = Math.min(96, Math.max(4, d.x));
    d.y = Math.min(94, Math.max(6, d.y));
    d.labelBelow = d.y < 14; // no room for a label above near the top edge
  }

  // Two close dots both labeling upward (or both downward) stack their labels
  // on top of each other. Flip the lower dot's label to the opposite side.
  const LABEL_CLEAR = 10; // percent — horizontal+vertical gap needed to avoid overlap
  for (let i = 0; i < dots.length; i++) {
    for (let j = i + 1; j < dots.length; j++) {
      const a = dots[i], b = dots[j];
      if (a.labelBelow !== b.labelBelow) continue;
      if (Math.abs(a.x - b.x) < LABEL_CLEAR && Math.abs(a.y - b.y) < LABEL_CLEAR) {
        const lower = a.y <= b.y ? b : a;
        lower.labelBelow = !lower.labelBelow;
      }
    }
  }

  return dots;
}

export function ObjectSelector({
  imageUrl, objects, selectedIds, onToggle, loading, error, onRefresh, maxSelect = MAX_DOTS,
}: ObjectSelectorProps) {
  const atCap = selectedIds.size >= maxSelect;
  const dots = useMemo(() => computeDots(objects ?? []), [objects]);

  return (
    <div className="object-select-stage">
      <div className="object-select-canvas">
        <img src={imageUrl} alt="Space" draggable={false} />
        {loading && (
          <div className="object-select-overlay">
            <span className="spinner" />
            <p className="caption" style={{ color: '#fff' }}>Identifying objects…</p>
          </div>
        )}
        {!loading && !error && dots.map(({ obj, x, y, labelBelow }) => {
          const selected = selectedIds.has(obj.id);
          const disabled = !selected && atCap;
          return (
            <button
              key={obj.id}
              type="button"
              className={`object-dot${selected ? ' object-dot--selected' : ''}`}
              style={{ left: `${x}%`, top: `${y}%` }}
              disabled={disabled}
              onClick={e => { e.stopPropagation(); onToggle(obj.id); }}
              aria-label={obj.label}
              title={disabled ? `Up to ${maxSelect} objects per generation` : obj.label}
            >
              {selected && (
                <span className={`object-dot-label${labelBelow ? ' object-dot-label--below' : ''}`}>
                  {obj.label}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {!loading && !error && objects && objects.length === 0 && (
        <div className="object-empty-state">
          No objects identified in this image.
        </div>
      )}

      {!loading && error && (
        <div className="object-empty-state object-empty-state--error">
          <span>{error}</span>
          <button type="button" className="btn btn-secondary btn-sm" onClick={onRefresh}>
            Retry
          </button>
        </div>
      )}

      {!loading && !error && objects && objects.length > 0 && (
        <div className="object-select-footer">
          <div className="object-chips-row">
            {objects.filter(o => selectedIds.has(o.id)).map(o => (
              <button
                key={o.id}
                type="button"
                className="object-chip"
                onClick={() => onToggle(o.id)}
                aria-label={`Deselect ${o.label}`}
              >
                {o.label} ×
              </button>
            ))}
            {selectedIds.size === 0 && (
              <span className="caption">Tap a dot to select an object</span>
            )}
            {atCap && (
              <span className="caption">Max {maxSelect} objects per generation</span>
            )}
          </div>
          <button
            type="button"
            className="object-refresh-btn"
            onClick={onRefresh}
            title="Re-detect objects"
            aria-label="Re-detect objects"
          >↻</button>
        </div>
      )}
    </div>
  );
}
